import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import {
  errorToastClassName,
  sonnerToast,
  successToastClassName,
  appToast as toast,
} from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { AlertIconLight } from "@images/svgIcons/alertIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { LoaderCircle } from "lucide-react"
import { useEffect, useImperativeHandle, useState } from "react"

export type ProgramSubscriberDeleteModalRef = {
  open: () => void
}

export type ProgramSubscriberDeleteModalProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  programId: string
  ref: React.Ref<ProgramSubscriberDeleteModalRef>
}

export function ProgramSubscriberDeleteModal({ subscriberDetails, programId, ref }: ProgramSubscriberDeleteModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [isSelected, setIsSelected] = useState({
    now: false,
    future: false,
  })

  const { sidebarNavigateBack } = useSidebar()
  const { removeSubscriber } = useProgramSubscriberActions(programId, subscriberDetails)

  useEffect(() => {
    if (subscriberDetails.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION) {
      setIsSelected({ now: true, future: false })
    }
  }, [showModal, subscriberDetails.subscriptionStatus])

  const onHide = () => {
    setIsSelected({ now: false, future: false })
    setShowModal(false)
  }

  const onSubmit = () => {
    setIsSubmitting(true)
    sonnerToast.promise(
      removeSubscriber(isSelected.future)
        .then(() => {
          toast.success(word("program.subscribers.subscriptionDetails.remove.success"), {
            classNames: successToastClassName(),
          })

          onHide()
          sidebarNavigateBack()
        })
        .catch(() => {
          toast.error(word("program.subscribers.subscriptionDetails.remove.error"), {
            classNames: errorToastClassName(),
          })
        })
        .finally(() => {
          setIsSubmitting(false)
        }),
    )
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const onSelectNow = () => {
    setIsSelected({ now: true, future: false })
  }

  const onSelectFuture = () => {
    if (subscriberDetails.subscriptionStatus !== ProgramSubscriptionStatus.PENDING_CANCELLATION)
      setIsSelected({ now: false, future: true })
    else
      toast.error(word("program.subscribers.subscriptionDetails.remove.pending_cancellation_error"), {
        classNames: errorToastClassName(),
      })
  }

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-col items-center justify-center gap-2 pt-4 text-center">
              <div className="flex w-full items-center justify-center">
                <AlertIconLight color="currentColor" className="text-textError" />
              </div>
              <Text fontStyle="TL" className="text-textDefault-heading">
                {word("program.subscribers.subscriptionDetails.remove.title")}
              </Text>
            </div>
          </DialogTitle>
          <DialogDescription>
            <Text fontStyle="BM" className="flex justify-center text-center text-textDefault-caption">
              {word("program.subscribers.subscriptionDetails.remove.description")}
            </Text>
          </DialogDescription>
        </DialogHeader>

        <div className="flex w-full gap-3">
          <div
            className={`flex basis-1/2 cursor-pointer flex-col gap-3 rounded-2xl border p-3 ${isSelected.now ? "border-borderPrimary" : "border-neutralColor-700"}`}
            onClick={onSelectNow}
          >
            <Text fontStyle="TS" className="text-center text-textDefault-heading">
              {word("program.subscribers.subscriptionDetails.remove.now")}
            </Text>
          </div>

          <div
            className={`flex basis-1/2 cursor-pointer flex-col gap-3 rounded-2xl border p-3 ${isSelected.future ? "border-borderPrimary" : "border-neutralColor-700"}`}
            onClick={onSelectFuture}
          >
            <Text fontStyle="TS" className="text-center text-textDefault-heading">
              {word("program.subscribers.subscriptionDetails.remove.future")}
            </Text>
            <Text fontStyle="BS" className="text-center text-textDefault-caption">
              {` ${word("program.subscribers.subscriptionDetails.remove.ending")} ${formatDateWithHyphen(dayjs(subscriberDetails.periodEndDate).format("L"))}`}
            </Text>
          </div>
        </div>

        <DialogFooter className="sm:justify-start">
          <Button variant="outline" size="lg" onClick={onHide} className="flex-1">
            {word("global.cancel")}
          </Button>

          <Button
            variant="primary"
            size="lg"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onSubmit()
            }}
            disabled={(!isSelected.future && !isSelected.now) || isSubmitting}
            isLoading={isSubmitting}
            className="flex-1"
          >
            {isSubmitting && <LoaderCircle className="animate-spin" />}
            {word("global.remove.standard")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
